/**
 * "Prices updating" surface. pSEO O2 (2026-06-24).
 *
 * Renders on /c/<slug> and /c/list/<slug> when the cache-only read
 * endpoint returns a genuine miss (404): the slug exists but its saved
 * comparison is not warmed yet (brand-new, or never refreshed). The
 * backend has ALREADY enqueued a background refresh on that 404
 * (routes/pseo.get_comparison), so the page must NOT run a blocking live
 * search in front of the visitor (that was the ~120s "page won't load"
 * hang). Instead we show this fast, honest state; once the warm lands the
 * next visit serves the real comparison (the read path is dynamic, so it
 * re-evaluates every request).
 *
 * Quiet amber, no error styling: the page is fine, it is simply catching
 * up on prices (NN #14 / Risk #13 — failure/transient surfaces stay quiet
 * on the brand). No auto-reload loop: a single manual "Check again" so a
 * warm that cannot complete (e.g. an Inngest outage) never hammers reload.
 */

"use client";

import Link from "next/link";
import type { Route } from "next";

import { SearchAgainBar } from "@/components/comparison/SearchAgainBar";

interface Props {
  /** The user's verbatim search query. Pre-fills SearchAgainBar. */
  query: string;
  /** Optional cleaner label from parsed_intent. */
  productLabel?: string;
}

export function ComparisonUpdating({ query, productLabel }: Props) {
  const headline = productLabel || query.trim() || "this comparison";

  return (
    <main className="cmp-surface relative mx-auto min-h-screen max-w-4xl bg-background px-5 pb-16 sm:px-8">
      <SearchAgainBar initialQuery={query} />

      <section className="mx-auto mt-12 max-w-2xl text-center">
        <p className="mb-4 inline-flex items-center gap-2 text-[12px] font-semibold uppercase tracking-[0.08em] text-label-progress">
          <span
            aria-hidden="true"
            className="inline-block h-1.5 w-1.5 animate-pulse rounded-full bg-amber-400"
          />
          Updating prices
        </p>
        <h1 className="text-3xl font-semibold leading-tight tracking-tight text-foreground sm:text-4xl">
          Octopusden is fetching the latest prices for{" "}
          <span className="text-foreground">&ldquo;{headline}&rdquo;</span>.
        </h1>
        <p className="mt-5 text-[15px] leading-relaxed text-muted-foreground sm:text-base">
          This usually takes a few moments. We check every Australian retailer
          in the background so the comparison is accurate when you see it.
          Check again shortly.
        </p>

        <div className="mt-8">
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="inline-flex items-center gap-1.5 rounded-xl border border-amber-500/30 bg-amber-500/5 px-4 py-2.5 text-sm font-medium text-foreground transition hover:border-amber-400/60 hover:bg-amber-500/10"
          >
            Check again
          </button>
        </div>
      </section>

      <div className="mx-auto mt-12 max-w-2xl text-center">
        <Link
          href={"/" as Route}
          className="inline-flex items-center gap-1.5 rounded-xl border border-border/60 bg-secondary/30 px-4 py-2.5 text-sm font-medium text-foreground backdrop-blur-md transition hover:border-accent/40 hover:bg-secondary/60"
        >
          ← Back to home
        </Link>
      </div>
    </main>
  );
}
